// src/content/figures/AppRepresentativeDecision.jsx
// Inline SVG infographic: does Article 27 GDPR apply to a non-EU app developer?
// Real text (indexable, selectable, screen-reader accessible).
// Explicit colours so it renders predictably regardless of theme tokens.

const C = {
  ink: '#1e293b',
  muted: '#5b6472',
  line: '#94a3b8',
  boxFill: '#f3f6fb',
  boxStroke: '#cbd5e1',
  white: '#ffffff',
  accent: '#186be6',
};

const q = { fill: C.boxFill, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const end = { fill: C.white, stroke: C.boxStroke, strokeWidth: 1.25, rx: 10 };
const win = { fill: C.accent, stroke: 'none', rx: 10 };
const t = { fontFamily: 'inherit', fontSize: 15, fill: C.ink };
const s = { fontFamily: 'inherit', fontSize: 13, fill: C.muted };
const lab = { fontFamily: 'inherit', fontSize: 13, fill: C.ink, fontWeight: 600 };
const onAccent = { fontFamily: 'inherit', fontSize: 15, fill: C.white };
const edge = { stroke: C.line, strokeWidth: 1.5, fill: 'none', markerEnd: 'url(#app27-arrow)' };

export default function AppRepresentativeDecision() {
  return (
    <svg
      viewBox="0 0 940 670"
      width="100%"
      role="img"
      aria-labelledby="app27-title app27-desc"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', maxWidth: 940 }}
    >
      <title id="app27-title">Does Article 27 GDPR apply to your app?</title>
      <desc id="app27-desc">
        Decision flow in three questions. One: do you have an establishment in the EU, such as an office or branch? If yes, Article 27 does not apply, because Article 3(2) covers only those not established in the EU. If no, question two: is your app available in EU storefronts, or do its SDKs track EU users for ads, attribution or analytics profiles? If no, you are outside Article 3(2) and need no EU representative; re-check if you add EU storefronts or an ad SDK. If yes, question three: are all three exemption conditions true, namely occasional processing, no large-scale special-category or criminal data, and unlikely to result in a risk? A few hundred installs is not occasional, because a live app with ad SDKs processes data regularly. If all three are true you are exempt, which is rare for a live app. Otherwise, appoint an EU representative in writing under Article 27(1). If you also offer the app to, or monitor, people in the UK, appoint a UK representative as well under UK GDPR Article 27.
      </desc>
      <defs>
        <marker id="app27-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={C.line} />
        </marker>
      </defs>

      <g transform="translate(10 0)">
      {/* Column guide: left x=0-250 (c=125) · centre x=250-670 (c=460) · right x=700-920 (c=810) */}

      {/* Q1 */}
      <rect x="260" y="20" width="400" height="64" {...q} />
      <text x="460" y="46" textAnchor="middle" {...t}>Do you have an establishment in the EU</text>
      <text x="460" y="67" textAnchor="middle" {...t}>(an office, branch or other establishment)?</text>

      {/* Q1 → Yes (left) */}
      <path d="M 260 52 L 125 52 L 125 140" {...edge} />
      <text x="135" y="104" {...lab}>Yes</text>
      <rect x="0" y="140" width="250" height="80" {...end} />
      <text x="125" y="168" textAnchor="middle" {...t}>Article 27 does not apply.</text>
      <text x="125" y="190" textAnchor="middle" {...s}>Article 3(2) covers only those</text>
      <text x="125" y="208" textAnchor="middle" {...s}>not established in the EU.</text>

      {/* Q1 → No (down) */}
      <path d="M 460 84 L 460 150" {...edge} />
      <text x="472" y="122" {...lab}>No</text>

      {/* Q2 */}
      <rect x="280" y="150" width="360" height="118" {...q} />
      <text x="460" y="176" textAnchor="middle" {...t}>Is your app available in EU storefronts,</text>
      <text x="460" y="197" textAnchor="middle" {...t}>or do its SDKs track EU users?</text>
      <text x="460" y="220" textAnchor="middle" {...s}>App Store: All Countries or Regions, or EU picks</text>
      <text x="460" y="239" textAnchor="middle" {...s}>Google Play: EU countries you selected</text>
      <text x="460" y="258" textAnchor="middle" {...s}>SDKs: ads, attribution or analytics profiles</text>

      {/* Q2 → No (right) */}
      <path d="M 640 209 L 810 209 L 810 290" {...edge} />
      <text x="652" y="200" {...lab}>No</text>
      <rect x="700" y="290" width="220" height="92" {...end} />
      <text x="810" y="316" textAnchor="middle" {...t}>Outside Article 3(2).</text>
      <text x="810" y="337" textAnchor="middle" {...s}>No EU representative needed.</text>
      <text x="810" y="355" textAnchor="middle" {...s}>Re-check if you add EU</text>
      <text x="810" y="372" textAnchor="middle" {...s}>storefronts or an ad SDK.</text>

      {/* Q2 → Yes (down) */}
      <path d="M 460 268 L 460 330" {...edge} />
      <text x="472" y="303" {...lab}>Yes</text>

      {/* Q3 */}
      <rect x="250" y="330" width="420" height="154" {...q} />
      <text x="460" y="356" textAnchor="middle" {...t}>Are all three true? (Article 27(2)(a))</text>
      <text x="460" y="380" textAnchor="middle" {...s}>1. Occasional: not regular, outside your normal activity</text>
      <text x="460" y="400" textAnchor="middle" {...s}>2. No large-scale special-category or criminal data</text>
      <text x="460" y="420" textAnchor="middle" {...s}>3. Unlikely to result in a risk to individuals</text>
      <rect x="266" y="432" width="388" height="40" fill="#fff4e0" stroke="none" rx="6" />
      <text x="460" y="448" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00" fontWeight="600">A few hundred installs is not "occasional".</text>
      <text x="460" y="464" textAnchor="middle" fontFamily="inherit" fontSize="12.5" fill="#7a4a00">A live app with ad SDKs processes data regularly.</text>

      {/* Q3 → Yes, all three (left) */}
      <path d="M 250 405 L 125 405 L 125 540" {...edge} />
      <text x="135" y="480" {...lab}>Yes, all three</text>
      <rect x="0" y="540" width="250" height="100" {...end} />
      <text x="125" y="568" textAnchor="middle" {...t}>Exempt.</text>
      <text x="125" y="590" textAnchor="middle" {...s}>Rare for a live app.</text>
      <text x="125" y="608" textAnchor="middle" {...s}>Document why; re-check when</text>
      <text x="125" y="626" textAnchor="middle" {...s}>you add an SDK.</text>

      {/* Q3 → No (down) */}
      <path d="M 460 484 L 460 540" {...edge} />
      <text x="472" y="516" {...lab}>No</text>

      {/* Result */}
      <rect x="310" y="540" width="300" height="80" {...win} />
      <text x="460" y="572" textAnchor="middle" {...onAccent} fontWeight="600">Appoint an EU representative</text>
      <text x="460" y="595" textAnchor="middle" {...onAccent}>in writing, under Article 27(1).</text>

      {/* Result ⇢ UK (dashed, right) */}
      <path d="M 610 580 L 700 580" {...edge} strokeDasharray="4 4" />
      <rect x="700" y="524" width="220" height="112" {...end} />
      <text x="810" y="550" textAnchor="middle" {...t}>UK in scope too?</text>
      <text x="810" y="572" textAnchor="middle" {...s}>Offer to, or monitor,</text>
      <text x="810" y="590" textAnchor="middle" {...s}>people in the UK? Appoint</text>
      <text x="810" y="608" textAnchor="middle" {...s}>a UK representative too</text>
      <text x="810" y="626" textAnchor="middle" {...s}>(UK GDPR Article 27).</text>
      </g>
    </svg>
  );
}
